/**
 * Extracts the version from branches starting with `release/`, `hotfix/`, or `future/`.
 */
const extractVersionFromBranch = (branchName: string): string | null => {
  const match = /^(release|hotfix|future)\/(.+)$/i.exec(branchName);

  if (!match?.[2]) {
    return null;
  }

  const versionMatch = /^v?(\d+(?:\.\d+)+)/i.exec(match[2]);

  return versionMatch?.[1] ?? null;
};

/**
 * Creates an obfuscated branch hint: the first letter of each word in the branch name and all numbers.
 */
const createObfuscatedBranchHint = (branchName: string): string => {
  const tokens = branchName.split(/[/\-_]+/).filter(Boolean);

  let hint = "";

  for (const token of tokens) {
    const numbers = token.match(/\d+/g);

    if (numbers) {
      hint += numbers.join("");
    }

    const firstLetter = /[a-zA-Z]/.exec(token);

    if (firstLetter) {
      hint += firstLetter[0].toLowerCase();
    }
  }

  return hint || "unknown";
};

/**
 * Generates a version label based on the branch name.
 */
export const getVersionLabel = (branchName: string): string => {
  if (branchName === "local") {
    return "local";
  }

  const normalizedBranch = branchName.trim().replace(/^refs\/heads\//, "");

  if (/^vnext$/i.test(normalizedBranch)) {
    return "next";
  }

  if (/^development$/i.test(normalizedBranch)) {
    return "development";
  }

  const version = extractVersionFromBranch(normalizedBranch);

  if (version) {
    return version;
  }

  return createObfuscatedBranchHint(normalizedBranch);
};
